import { valibotResolver } from "@hookform/resolvers/valibot";
import { PlusCircle } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAgencyClientsMutations from "../../datahooks/useAgencyClientsMutations";
import { AgencyClientSchema, type AgencyClientSchemaType } from "../../schema";

export default function NewClientButton({ size }: { size: "sm" | "md" }) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);

  const { addAgencyClient, isAddingAgencyClient } = useAgencyClientsMutations();

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AgencyClientSchemaType>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
    resolver: valibotResolver(AgencyClientSchema),
  });

  function onSubmit(newClient: AgencyClientSchemaType) {
    addAgencyClient({ newClient }).then(() => {
      setIsOpen(false);
    });
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;
  const Close = isDesktop ? DialogClose : DrawerClose;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen} onAfterClose={reset}>
      <Trigger asChild>
        <Button leftIcon={<PlusCircle weight="fill" />} size={size}>
          Invite client
        </Button>
      </Trigger>
      <Content className="md:w-[448px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title>Invite a client</Title>
          <Description className="mb-8">
            Invite an agency client to this workspace. They will get an email
            with a link to join.
          </Description>

          {/* Name */}
          <FormField
            label="First name"
            htmlFor="firstName"
            error={errors.firstName?.message}
            className="mb-3"
          >
            <Input
              id="firstName"
              placeholder="Enter first name"
              {...register("firstName")}
            />
          </FormField>

          <FormField
            label="Last name"
            htmlFor="url"
            error={errors.lastName?.message}
            className="mb-3"
          >
            <Input
              id="url"
              placeholder="Enter last name"
              {...register("lastName")}
            />
          </FormField>

          <FormField
            label="Email"
            htmlFor="email"
            error={errors.email?.message}
            className="mb-3"
          >
            <Input
              id="email"
              placeholder="Enter client email"
              {...register("email")}
            />
          </FormField>

          <div className="mt-8 flex justify-between">
            <Close asChild>
              <Button variant="secondary-black" disabled={isAddingAgencyClient}>
                Cancel
              </Button>
            </Close>

            <Button type="submit" isLoading={isAddingAgencyClient}>
              Save
            </Button>
          </div>
        </form>
      </Content>
    </Component>
  );
}
